import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getLscScheduleSource,
  getLscSyncStatus,
  setLscAutoSyncSafeApply,
  syncLscScheduleSource,
} from '../api';
import type { LscScheduleCheckVerdict, LscScheduleSourceStatus, LscSyncStatus } from '../types';
import { displaySeasonLabel } from '../seasonDisplay';
import CsvImportPanel from './CsvImportPanel';
import NeonSportzUrlImportPanel from './NeonSportzUrlImportPanel';
import ScheduleSourcePanel from './ScheduleSourcePanel';
import ScheduleSyncStatusPanel from './ScheduleSyncStatusPanel';

export type ImportSyncConsoleProps = {
  leagueId: string;
};

function positionLabel(season: string, week?: string | null): string {
  const stage = (week || '').trim();
  if (season && stage) return `${season}, ${stage}`;
  if (season) return season;
  if (stage) return stage;
  return 'an unknown spot';
}

function localTime(iso?: string | null): string {
  return iso
    ? new Date(iso).toLocaleString(undefined, {
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';
}

/** Substitutes the check times into a sentence the server already wrote. */
function renderServerVerdict(verdict?: LscScheduleCheckVerdict | null): string | null {
  const text = (verdict?.verdict_text || '').trim();
  if (!text) return null;
  return text
    .replace('{when}', localTime(verdict?.verdict_checked_at))
    .replace('{next}', localTime(verdict?.verdict_next_check_at))
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function ImportSyncConsole({ leagueId }: ImportSyncConsoleProps) {
  const [status, setStatus] = useState<LscSyncStatus | null>(null);
  const [sourceStatus, setSourceStatus] = useState<LscScheduleSourceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [safeApplySaving, setSafeApplySaving] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [syncStatus, scheduleSource] = await Promise.all([
        getLscSyncStatus(leagueId),
        getLscScheduleSource(leagueId),
      ]);
      setStatus(syncStatus);
      setSourceStatus(scheduleSource);
    } catch {
      setNotice('Could not load schedule status.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void load();
  }, [load]);

  const connected = Boolean(sourceStatus?.schedule_source?.enabled || status?.schedule_source?.enabled);
  const lscSeason = displaySeasonLabel(
    status?.active_season_label || sourceStatus?.active_season_label
  );
  const providerSeason = displaySeasonLabel(
    status?.detected_season_label || status?.schedule_source?.last_detected_season
  );
  const lscWeek = (status?.active_stage_label || sourceStatus?.active_stage_label || '').trim();
  const providerWeek = (
    status?.detected_stage_label ||
    status?.provider_stage_label ||
    status?.schedule_source?.last_detected_week ||
    ''
  ).trim();
  const seasonGap = Boolean(status?.season_changed || status?.rollover_review_required);
  const weekDrift = Boolean(
    lscWeek && providerWeek && lscWeek.toLowerCase() !== providerWeek.toLowerCase()
  );
  const repairNeeded = Boolean(status?.schedule_source?.sync_status === 'needs_attention');
  const attentionMessage = (status?.schedule_source?.attention_message || '').trim();
  const scheduleNote = (status?.schedule_source?.schedule_note || '').trim();
  const verdictLine = renderServerVerdict(status?.schedule_verdict || status?.schedule_source?.schedule_verdict);

  const banner = useMemo(() => {
    if (!connected || loading) return null;
    if (seasonGap) {
      return {
        testId: 'import-sync-season-banner',
        text: `NeonSportz has moved to ${providerSeason || 'a new season'}. Review that season before LSC follows it.`,
      };
    }
    if (weekDrift || status?.is_stale) {
      return {
        testId: 'import-sync-stale-banner',
        text: 'NeonSportz is ahead of the week LSC is using.',
      };
    }
    if (repairNeeded && attentionMessage) {
      return {
        testId: 'import-sync-repair-banner',
        text: attentionMessage,
      };
    }
    return null;
  }, [
    connected,
    loading,
    seasonGap,
    providerSeason,
    weekDrift,
    status?.is_stale,
    repairNeeded,
    attentionMessage,
  ]);

  const primary = useMemo(() => {
    if (!connected) {
      return { label: 'Connect NeonSportz', action: 'connect' as const };
    }
    if (seasonGap) {
      return { label: 'Review the new season', action: 'review' as const };
    }
    if (weekDrift || status?.is_stale || repairNeeded) {
      return { label: 'Review changes', action: 'review' as const };
    }
    return { label: syncing ? 'Syncing…' : 'Sync now', action: 'sync' as const };
  }, [connected, seasonGap, weekDrift, status?.is_stale, repairNeeded, syncing]);

  const onPrimary = async () => {
    setNotice(null);
    if (primary.action === 'connect') {
      setAdvancedOpen(true);
      return;
    }
    if (primary.action === 'review') {
      setReviewing(true);
      return;
    }
    setSyncing(true);
    try {
      const result = await syncLscScheduleSource(leagueId, { apply: false });
      await load();
      const line = renderServerVerdict(result.schedule_verdict);
      setNotice(line);
    } catch {
      setNotice('Could not check NeonSportz.');
    } finally {
      setSyncing(false);
    }
  };

  const onToggleSafeApply = async (enabled: boolean) => {
    setSafeApplySaving(true);
    try {
      const next = await setLscAutoSyncSafeApply(leagueId, enabled);
      setSourceStatus(next);
    } catch {
      setNotice('Could not update automatic updates.');
    } finally {
      setSafeApplySaving(false);
    }
  };

  const statusLine = `Your league is on ${positionLabel(lscSeason, lscWeek)}. NeonSportz is on ${positionLabel(
    providerSeason,
    providerWeek
  )}.`;

  return (
    <div className="space-y-6" data-testid="import-sync-console">
      <section className="card space-y-4" data-testid="import-sync-status-card">
        <div>
          <h2 className="text-lg font-semibold text-white">Schedule status</h2>
          {loading ? (
            <p className="text-sm text-secondary-400 mt-2">Loading schedule status…</p>
          ) : (
            <p className="text-sm text-secondary-100 mt-2" data-testid="import-sync-status-line">
              {connected ? statusLine : 'NeonSportz is not connected yet.'}
            </p>
          )}
        </div>

        {banner ? (
          <div
            className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100"
            data-testid={banner.testId}
          >
            {banner.text}
          </div>
        ) : null}

        {verdictLine && !notice ? (
          <p className="text-sm text-secondary-300" data-testid="import-sync-verdict">
            {verdictLine}
          </p>
        ) : null}

        {scheduleNote && !banner ? (
          <p className="text-sm text-secondary-300" data-testid="import-sync-schedule-note">
            {scheduleNote}
          </p>
        ) : null}

        {notice ? (
          <p className="text-sm text-secondary-300" data-testid="import-sync-notice">
            {notice}
          </p>
        ) : null}

        <button
          type="button"
          className="btn-primary"
          disabled={loading || syncing}
          onClick={() => void onPrimary()}
          data-testid="import-sync-primary-action"
        >
          {primary.label}
        </button>

        {connected && sourceStatus?.auto_sync_safe_apply_available !== false ? (
          <label className="flex items-start gap-3 text-sm text-secondary-200">
            <input
              type="checkbox"
              className="mt-1"
              checked={Boolean(sourceStatus?.auto_sync_safe_apply_enabled)}
              disabled={safeApplySaving}
              onChange={(event) => void onToggleSafeApply(event.target.checked)}
              data-testid="import-sync-safe-apply-toggle"
            />
            <span>Keep my schedule current automatically</span>
          </label>
        ) : null}
      </section>

      {reviewing ? (
        <div data-testid="import-sync-review">
          <NeonSportzUrlImportPanel leagueId={leagueId} onImportSuccess={() => void load()} />
        </div>
      ) : null}

      <section data-testid="import-sync-advanced">
        <button
          type="button"
          className="btn-secondary text-sm"
          onClick={() => setAdvancedOpen((open) => !open)}
          data-testid="import-sync-advanced-toggle"
        >
          {advancedOpen ? 'Hide advanced' : 'Advanced'}
        </button>
        {advancedOpen ? (
          <div className="space-y-6 mt-4">
            <ScheduleSourcePanel leagueId={leagueId} placement="details" />
            <ScheduleSyncStatusPanel leagueId={leagueId} detailsOnly />
            <CsvImportPanel leagueId={leagueId} />
          </div>
        ) : null}
      </section>
    </div>
  );
}
